// admin-panel/src/pages/InquiryDetail.jsx
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inquiriesApi } from '../lib/api';
import { StatusBadge } from './Dashboard';
import {
  ArrowLeft, Phone, Mail, MapPin, Building2, MessageCircle,
  Send, FileText, Clock, User, AlertTriangle, CheckCircle,
  PhoneCall, MessageSquare, Calendar, Plus
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUSES  = ['new','contacted','in_progress','quoted','converted','closed','spam'];
const PRIORITIES = ['low','medium','high','urgent'];
const NOTE_TYPES = [
  { key: 'note', label: 'Note', icon: MessageSquare },
  { key: 'call', label: 'Call', icon: PhoneCall },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'meeting', label: 'Meeting', icon: Calendar },
];

const priorityColors = { low: 'text-gray-400', medium: 'text-blue-400', high: 'text-orange-400', urgent: 'text-red-400' };
const noteTypeIcon = { note: MessageSquare, call: PhoneCall, email: Mail, whatsapp: MessageCircle, meeting: Calendar, status_change: AlertTriangle };

export default function InquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [waMsg, setWaMsg] = useState('');

  const { data: inq, isLoading } = useQuery({
    queryKey: ['inquiry', id],
    queryFn: () => inquiriesApi.get(id),
  });

  const updateMut = useMutation({
    mutationFn: (data) => inquiriesApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['inquiry', id]); qc.invalidateQueries(['inquiries']); toast.success('Updated'); },
  });

  const noteMut = useMutation({
    mutationFn: (data) => inquiriesApi.addNote(id, data),
    onSuccess: () => { qc.invalidateQueries(['inquiry', id]); setNote(''); toast.success('Note added'); },
  });

  const waMut = useMutation({
    mutationFn: (data) => inquiriesApi.sendWhatsApp(id, data),
    onSuccess: (res) => { window.open(res.wa_url, '_blank'); qc.invalidateQueries(['inquiry', id]); toast.success('WhatsApp opened'); },
  });

  if (isLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!inq) return <div className="text-center py-20 text-[#555]">Inquiry not found.</div>;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/inquiries')} className="p-2 rounded-xl bg-[#151515] border border-white/5 text-[#888] hover:text-white transition-all">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">{inq.name}</h1>
            <StatusBadge status={inq.status} />
            <span className={`text-xs font-medium capitalize ${priorityColors[inq.priority]}`}>{inq.priority}</span>
          </div>
          <p className="text-sm text-[#555] mt-0.5">#{inq.id} · {new Date(inq.created_at).toLocaleString('en-IN')}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/quotes/new?inquiry=${id}`}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-[#D4AF37]/30 text-[#D4AF37] rounded-xl hover:bg-[#D4AF37]/10 transition-all">
            <FileText size={15} /> Create Quote
          </Link>
          <button onClick={() => waMut.mutate({ message: waMsg || undefined })}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#22c55e] transition-all">
            <MessageCircle size={15} /> WhatsApp
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: Details */}
        <div className="space-y-4">
          {/* Contact Card */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Contact Info</h3>
            <div className="space-y-3">
              {[
                { icon: User,     label: 'Name',    value: inq.name },
                { icon: Phone,    label: 'Phone',   value: inq.phone, href: `tel:${inq.phone}` },
                { icon: Mail,     label: 'Email',   value: inq.email, href: `mailto:${inq.email}` },
                { icon: Building2, label: 'Company', value: inq.company },
                { icon: MapPin,   label: 'Source',  value: inq.source },
              ].filter(r => r.value).map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex gap-3">
                  <Icon size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs text-[#555]">{label}</div>
                    {href ? (
                      <a href={href} className="text-sm text-white hover:text-[#D4AF37] transition-colors">{value}</a>
                    ) : (
                      <div className="text-sm text-white capitalize">{value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status/Priority */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white">Management</h3>
            <div>
              <label className="block text-xs text-[#555] mb-1.5">Status</label>
              <select value={inq.status} onChange={e => updateMut.mutate({ status: e.target.value })}
                className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30">
                {STATUSES.map(s => <option key={s} value={s} className="bg-[#1A1A1A] capitalize">{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#555] mb-1.5">Priority</label>
              <select value={inq.priority} onChange={e => updateMut.mutate({ priority: e.target.value })}
                className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30">
                {PRIORITIES.map(p => <option key={p} value={p} className="bg-[#1A1A1A] capitalize">{p}</option>)}
              </select>
            </div>
          </div>

          {/* Service */}
          {inq.service && (
            <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
              <div className="text-xs text-[#555] mb-1">Interested In</div>
              <div className="text-sm text-white font-medium">{inq.service.title}</div>
            </div>
          )}

          {/* WA Quick Send */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Quick WhatsApp</h3>
            <textarea value={waMsg} onChange={e => setWaMsg(e.target.value)} rows={3}
              placeholder={`Hi ${inq.name}, thank you for your inquiry…`}
              className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#25D366]/30 resize-none mb-3" />
            <button onClick={() => waMut.mutate({ message: waMsg || undefined })} disabled={waMut.isPending}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#25D366] hover:bg-[#22c55e] text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50">
              <MessageCircle size={15} /> Open WhatsApp
            </button>
          </div>
        </div>

        {/* Right: Message + Notes Timeline */}
        <div className="lg:col-span-2 space-y-4">
          {/* Original Message */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={15} className="text-[#D4AF37]" />
              <h3 className="text-sm font-semibold text-white">Original Message</h3>
              {inq.subject && <span className="text-xs text-[#555] ml-auto">{inq.subject}</span>}
            </div>
            <p className="text-sm text-[#CCCCCC] leading-relaxed bg-[#1A1A1A] rounded-xl p-4">{inq.message}</p>
          </div>

          {/* Add Note */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Add Activity</h3>
            <div className="flex gap-2 mb-3 flex-wrap">
              {NOTE_TYPES.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setNoteType(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${noteType === key ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'border-white/5 text-[#666] hover:text-white'}`}>
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder={`Add a ${noteType} note…`}
                className="flex-1 bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none" />
              <button onClick={() => noteMut.mutate({ note, type: noteType })} disabled={!note.trim() || noteMut.isPending}
                className="px-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] rounded-xl hover:bg-[#D4AF37]/20 disabled:opacity-40 transition-all">
                <Send size={16} />
              </button>
            </div>
          </div>

          {/* Notes Timeline */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Activity Timeline</h3>
            {inq.notes?.length > 0 ? (
              <div className="space-y-3">
                {inq.notes.map((n) => {
                  const Icon = noteTypeIcon[n.type] || MessageSquare;
                  return (
                    <div key={n.id} className="flex gap-3">
                      <div className="w-7 h-7 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={12} className="text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 bg-[#1A1A1A] rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-[#D4AF37] capitalize">{n.type?.replace('_', ' ')}</span>
                          <span className="text-xs text-[#444]">{new Date(n.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                        </div>
                        <p className="text-sm text-[#CCCCCC]">{n.note}</p>
                        {n.user && <p className="text-xs text-[#555] mt-1">by {n.user.name}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-[#555] text-sm">
                <Clock size={28} className="mx-auto mb-2 text-[#333]" />
                No activity yet. Add your first note above.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
